export type OrbitLayer = "inner" | "middle" | "outer";

export type Vec3 = { x: number; y: number; z: number };

export type WorldLook = {
  colorA: string;
  colorB: string;
  atmosphere: string;
  seed: number;
  radius: number;
  ring?: boolean;
  habitable?: boolean;
};

export type NetworkNode = {
  id?: string;
  name: string;
  metricLabel: string;
  orbitLayer: OrbitLayer;
  colorHex: string;
  cluster?: string;
  signalLabel?: string;
  why?: string;
  relation?: string;
};

export type SpatialNode = {
  id: string;
  signalId?: string;
  name: string;
  metricLabel: string;
  orbitLayer: OrbitLayer;
  color: string;
  cluster: string;
  signalLabel: string;
  why: string;
  relation: string;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  orbitRadius: number;
  orbitAngle: number;
  orbitSpeed: number;
  spawnedAt: number;
  z: number;
  targetZ: number;
  rest: Vec3;
  axis: Vec3;
  look: WorldLook;
  parentId: string;
  parentX: number;
  parentY: number;
  parentZ: number;
  generationDepth: number;
};

export type GraphLevel = {
  depth: number;
  centerTopic: string;
  centerSignalId?: string;
  diveSignalId?: string;
  hostPlanetId?: string;
  centerX: number;
  centerY: number;
  centerZ: number;
  centerLook: WorldLook;
  centerSpawnedAt: number;
  nodes: SpatialNode[];
};

export type PulseCam = {
  x: number;
  y: number;
  z: number;
  zoom: number;
  targetX: number;
  targetY: number;
  targetZ: number;
  targetZoom: number;
  yaw: number;
  pitch: number;
  targetYaw: number;
  targetPitch: number;
};

export function emptyCam(): PulseCam {
  return {
    x: 0,
    y: 0,
    z: 0,
    zoom: 1,
    targetX: 0,
    targetY: 0,
    targetZ: 0,
    targetZoom: 1,
    yaw: 0.55,
    pitch: 0.28,
    targetYaw: 0.55,
    targetPitch: 0.28,
  };
}

export function camLook(c: PulseCam) {
  return { x: c.x / SCALE, y: -c.y / SCALE, z: c.z / SCALE };
}

export function camRadius(c: PulseCam) {
  return 24 / Math.max(0.2, c.zoom);
}

export function camEye(c: PulseCam) {
  const look = camLook(c);
  const r = camRadius(c);
  const cp = Math.cos(c.pitch);
  return {
    x: look.x + r * Math.sin(c.yaw) * cp,
    y: look.y + r * Math.sin(c.pitch),
    z: look.z + r * Math.cos(c.yaw) * cp,
  };
}

export const SCALE = 42;
export const CAM_LERP = 0.06;
export const ZOOM_MIN = 0.2;
export const ZOOM_MAX = 5;
export const DIVE_ZOOM = 5;
export const SETTLE_ZOOM = 1.2;
export const HIT_RADIUS = 34;
export const EMPTY_RADIUS = 56;
export const CENTER_RADIUS = 48;

export function hitRadiusFor(layer: OrbitLayer) {
  if (layer === "inner") return 40;
  if (layer === "middle") return 32;
  return 24;
}
export const ORBIT_LERP = 0.04;

export const COLORS = {
  cyan: "#73e0d2",
  gold: "#f0bd68",
  violet: "#ca8cff",
  archive: "#cf9464",
} as const;
